import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { login } from "@/api/endpoints";
import { usePageTitle } from "@/hooks/usePageTitle";

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

export function Login(): JSX.Element {
  usePageTitle("Sign in");
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(email, password);
      const state = location.state as LocationState | null;
      const from = state?.from;
      const dest = from
        ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
        : "/";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Sign-in failed";
      setError(msg);
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="card w-full max-w-sm space-y-4"
        noValidate
        aria-describedby={error ? "login-error" : undefined}
      >
        <header className="space-y-2">
          <div className="flex items-center gap-2.5">
            <svg
              viewBox="0 0 24 24"
              width="28"
              height="28"
              aria-hidden="true"
              className="text-severity-ok"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2.7c3.4 4.2 6.5 7.9 6.5 11.4a6.5 6.5 0 1 1-13 0C5.5 10.6 8.6 6.9 12 2.7z" />
              <path d="M9.2 14.2a2.9 2.9 0 0 0 2.4 2.9" opacity="0.7" />
            </svg>
            <h1 className="text-2xl font-semibold tracking-tight">AquaTrack</h1>
          </div>
          <p className="text-sm text-ink-300">
            Florida drinking-water compliance, tracked. Sign in to continue.
          </p>
        </header>

        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && (
          <p id="login-error" className="text-sm text-severity-high" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-sm text-ink-400 text-center">
          <Link to="/forgot-password" className="hover:text-ink-100">
            Forgot your password?
          </Link>
        </p>
      </form>
    </main>
  );
}
